import { Collaborate } from '@desygner/ui-editor-popup-menu';
import React, { useCallback, useState } from 'react';

import { initShareMembersPanel } from 'assets/js/editor/ui/top-submenu/init-top-bar';
import ReactAppBuilder from 'src/utils/react-app-builder';

import {
  addUsers,
  getSharedMembers,
  normalizeSearchMember,
  removeUsers,
  searchMembers,
  setTeamSharedProject
} from './helper';

export const appContainer = 'collaboration-app';

class CollaborationApp extends ReactAppBuilder {
  constructor ({ appId, targetNodeSelector }) {
    super({ appId, targetNodeSelector });
    this.sharedUsers = undefined;
    this.initialSharedUsers = undefined;
  }

  mountApp = () => {
    super.mountApp(this.App);
  };

  closeApp = () => {
    super.closeApp(this.App);
  };

  loadSharedUser = async () => {
    if (!this.sharedUsers) {
      const sharedMembers = await getSharedMembers();
      if (sharedMembers) {
        this.sharedUsers = sharedMembers.map((member) => ({
          id: member.id,
          name: member.nickname,
          sourceUrl: member.profile_picture,
          permissionId: member.permissionId,
          permissionType: member.permissionType
        }));
        this.initialSharedUsers = [...this.sharedUsers];
      } else {
        this.sharedUsers = [];
        this.initialSharedUsers = [];
      }
    }

    return this.sharedUsers.filter((user) => user.permissionType !== 'owner');
  };

  searchItems = async (value, {
    limit = 30,
    offset = 0
  } = {
    limit: 30,
    offset: 0
  }) => {
    const members = await searchMembers(value, {
      limit,
      offset
    });

    if (!members) {
      return [];
    }

    return members
      .map(normalizeSearchMember)
      .filter((member) =>
        this.sharedUsers === undefined
          ? true
          : !this.sharedUsers.find((user) => member.id === user.id)
      );
  };

  confirmSharedUsers = async () => {
    if (this.initialSharedUsers === undefined || this.sharedUsers === undefined) {
      return;
    }

    const removedUsers = this.initialSharedUsers.filter(
      (initialUser) =>
        !this.sharedUsers.find((sharedUser) => sharedUser.id === initialUser.id)
    );

    const addedUsers = this.sharedUsers.filter(
      (sharedUser) =>
        !this.initialSharedUsers.find(
          (initialUser) => sharedUser.id === initialUser.id
        )
    );

    if (addedUsers.length > 0 || removedUsers.length > 0) {
      await Promise.all([addUsers(addedUsers), removeUsers(removedUsers)]);
      const newNumberOfSharedMember =
        this.initialSharedUsers.length - removedUsers.length + addedUsers.length;
      setTeamSharedProject(newNumberOfSharedMember > 1);
      this.sharedUsers = undefined;
      await initShareMembersPanel();
    }
    INKIVE.EditorFacade.canvas.call('advice.show', 'already-saved');
    INKIVE.Modal.togglePageLoader(false);
  };

  mapProps = () => {
    const [{ offset, limit }, updateSearchSettings] = useState({
      offset: 0,
      limit: 30
    });

    const onSearchTermChange = useCallback(async (value) => {
      const searchResponse = await this.searchItems(value);

      updateSearchSettings({ offset: 0, limit });

      return searchResponse;
    }, []);

    const onSearchTermPaginate = async (value) => {
      const newOffset = offset + limit;

      updateSearchSettings({ offset: newOffset, limit });

      const searchResponse = await this.searchItems(value, {
        offset: newOffset,
        limit
      });

      return searchResponse;
    };

    return {
      loadUsers: this.loadSharedUser,
      onSearchTermChange,
      onSearchTermPaginate,

      onAddUser: async (item) => {
        this.sharedUsers.push(item);
      },

      onRemoveUser: async (item) => {
        this.sharedUsers = this.sharedUsers.filter((i) => i.id !== item.id);
      },

      closeModal: async () => {
        INKIVE.Modal.togglePageLoader(true);
        this.confirmSharedUsers();
        this.closeApp();
      }
    };
  };

  App = () => {
    const props = this.mapProps();

    return <Collaborate {...props} />;
  };
}

export default new CollaborationApp({
  appId: appContainer,
  targetNodeSelector: '.collaboration-modal'
});
